import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import { TrainingNavItem } from '../../services/trainingService';

interface HelpNavigationProps {
  items: TrainingNavItem[];
  activeDocId: string;
  onSelectDoc: (docId: string) => void;
}

/**
 * Contents sidebar for the training manual.
 */
export const HelpNavigation: React.FC<HelpNavigationProps> = ({
  items,
  activeDocId,
  onSelectDoc,
}) => {
  // Group items by category
  const groupedItems = items.reduce(
    (acc, item) => {
      const category = item.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    },
    {} as Record<string, TrainingNavItem[]>
  );

  const categories = Object.keys(groupedItems);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        // Semantic token, not a colour name. The reading pane uses
        // background.default, so the sidebar takes background.paper to stay
        // distinct from it in both light and dark themes.
        //
        // No borderRight: the divider between the columns is the drawer's
        // layout concern and is set on the wrapper in HelpDrawer. Setting it
        // here too drew two borders, and would draw one on mobile where this
        // component fills the width.
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={600} color="primary">
          Training Manual
        </Typography>
      </Box>

      {categories.map((category, index) => (
        <Box key={category}>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: 'action.hover' }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              {category}
            </Typography>
          </Box>
          <List disablePadding>
            {groupedItems[category].map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={activeDocId === item.id}
                  onClick={() => onSelectDoc(item.id)}
                  sx={{
                    py: 1.5,
                    px: 3,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemText-primary': {
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontSize: '0.875rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {index < categories.length - 1 && <Divider />}
        </Box>
      ))}
    </Box>
  );
};
